
import { safePlayMediaElement } from '../media/playbackUtils';
import { MiddlewareRegistry } from '../redux';

import { PLAY_SOUND, STOP_SOUND } from './actionTypes';
import logger from './logger';

/**
 * Implements the entry point of the middleware of the feature base/media.
 *
 * @param {Store} store - The redux store.
 * @returns {Function}
 */
MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
    case PLAY_SOUND:
        _playSound(store, action.soundId);
        break;
    case STOP_SOUND:
        _stopSound(store, action.soundId);
        break;
    }

    return next(action);
});

/**
 * Plays sound from audio element registered in the Redux store.
 *
 * @param {Store} store - The Redux store instance.
 * @param {string} soundId - Audio element identifier.
 * @private
 * @returns {void}
 */
function _playSound({ getState }, soundId) {
    const sounds = getState()['features/base/sounds'];
    const sound = sounds.get(soundId);

    if (sound) {
        if (sound.audioElement) {
            _safePlaySoundElement(sound.audioElement, soundId);
        } else {
            logger.warn(`PLAY_SOUND: sound not loaded yet for id: ${soundId}`);
        }
    } else {
        logger.warn(`PLAY_SOUND: no sound found for id: ${soundId}`);
    }
}

/**
 * Plays a sound element safely and classifies expected browser play errors.
 *
 * @param {Object} audioElement - The sound audio element.
 * @param {string} soundId - The sound identifier used for log context.
 * @private
 * @returns {void}
 */
function _safePlaySoundElement(audioElement, soundId) {
    if (!audioElement || typeof audioElement.play !== 'function') {
        return;
    }

    safePlayMediaElement(audioElement, {
        onAutoplayBlocked: error => {
            logger.warn(`PLAY_SOUND: autoplay blocked for id: ${soundId}`, error);
        },
        onError: error => {
            logger.error(`PLAY_SOUND: failed for id: ${soundId}`, error);
        }
    });
}

/**
 * Stop sound from audio element registered in the Redux store.
 *
 * @param {Store} store - The Redux store instance.
 * @param {string} soundId - Audio element identifier.
 * @private
 * @returns {void}
 */
function _stopSound({ getState }, soundId) {
    const sounds = getState()['features/base/sounds'];
    const sound = sounds.get(soundId);

    if (sound) {
        const { audioElement } = sound;

        if (audioElement) {
            audioElement.stop();
        } else {
            logger.warn(`STOP_SOUND: sound not loaded yet for id: ${soundId}`);
        }
    } else {
        logger.warn(`STOP_SOUND: no sound found for id: ${soundId}`);
    }
}
